import { css } from '@emotion/react'
import { useTranslation } from 'next-i18next'

import { useMarket } from '@/utils/multi-markets/context'
import type { Locale } from '@/utils/i18n/types'

import { Price } from '@/service'

import { mediaUp } from '@/styles/media'
import { colors } from '@/styles/theme'
import { Text2 } from '@/styles/typography'

interface Props {
  price: Price
  isAbsolute?: boolean
  noMargin?: boolean
  background?: string
  color?: string
  periodInDays?: number
}

const formatPeriodInDays = (periodInDays: number, locale: Locale) => {
  const formattedPeriod =
    locale === 'ar'
      ? new Intl.NumberFormat('ar-EG', { useGrouping: false }).format(
          periodInDays
        )
      : periodInDays

  const unit = (() => {
    switch (locale) {
      case 'pl':
        return periodInDays === 1 ? 'dzień' : 'dni'
      case 'de':
        return periodInDays === 1 ? 'Tag' : 'Tage'
      case 'ar':
        if (periodInDays === 1) return 'يوم'
        if (periodInDays === 2) return 'يومان'
        if (periodInDays >= 3 && periodInDays <= 10) return 'أيام'
        return 'يومًا'
      case 'en':
      default:
        return periodInDays === 1 ? 'day' : 'days'
    }
  })()

  return `${formattedPeriod} ${unit}`
}

function PriceTag(props: Props) {
  const {
    price,
    isAbsolute = true,
    noMargin = false,
    background = colors.white,
    color = colors.twilightBlue,
    periodInDays
  } = props
  const { t } = useTranslation('common')
  const { locale } = useMarket()

  const formattedPrice =
    locale === 'ar'
      ? new Intl.NumberFormat('ar-EG').format(price?.amount)
      : price?.amount

  const currencyText = t(`currency${price?.currency}` as any)

  const periodText =
    periodInDays !== undefined ? formatPeriodInDays(periodInDays, locale) : 'h'

  return (
    <Text2 css={styles.priceTag(isAbsolute, noMargin, background, color)}>
      <strong>
        {formattedPrice} {currencyText} / {periodText}
      </strong>
    </Text2>
  )
}

const styles = {
  priceTag: (
    isAbsolute: boolean,
    noMargin: boolean,
    background: string,
    color: string
  ) => css`
    ${isAbsolute &&
    css`
      position: absolute;
      top: 0;
      left: 0;
    `}
    margin: ${noMargin ? '0' : '14px'};
    padding: 4px 8px;
    border-radius: 12px;
    background-color: ${background};
    color: ${color};

    ${mediaUp('lg')} {
      margin: ${noMargin ? '0' : '30px'};
      padding: 5px 12px;
      border-radius: 17px;
    }

    html[lang^='ar'] & {
      left: unset;
      right: 0;
    }
  `
}

const hexToRgb = (hex: string) => {
  const bigint = parseInt(hex.replace('#', ''), 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r}, ${g}, ${b}`
}

export default PriceTag
